import styled, { keyframes, css } from "styled-components";
import { tokens, surface, hairline } from "../../styles/designTokens";

const meshDrift = keyframes`
  0%   { transform: translate(0, 0) scale(1); }
  50%  { transform: translate(-20px, 18px) scale(1.05); }
  100% { transform: translate(0, 0) scale(1); }
`;
const pulseDot = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.9; }
  50%      { transform: scale(1.15); opacity: 0.5; }
`;
const fadeInOnly = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;
const eyebrow = css`
  ${mono};
  font-size: ${tokens.fsEyebrow};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.text};
  opacity: 0.45;
`;
const cardBase = (theme) => css`
  background: ${surface(theme, 0)};
  border: 1px solid ${hairline(theme)};
  border-radius: ${tokens.radiusLg};
  transition: border-color ${tokens.durBase} ${tokens.easeOut},
              background ${tokens.durBase} ${tokens.easeOut};
`;

/**
 * Estilos da tela PLCom — mesma linguagem visual da aba Reforma Tributária
 * (ver ../reforma-tributaria/styles.js), reduzida aos blocos usados aqui:
 * header, KPI cards, section-title, info-banner e rate-table.
 */
export const Container = styled.div`
  width: 100%;
  max-width: 90em;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0 2em 2em 2em;
  position: relative;
  min-height: 100vh;
  overflow-y: auto;
  animation: ${fadeInOnly} ${tokens.durSlow} ${tokens.easeOut};

  &::before {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background:
      radial-gradient(800px 600px at 15% 10%, ${tokens.brandSoft} 0%, transparent 60%),
      radial-gradient(700px 500px at 85% 70%, rgba(6, 182, 212, 0.05) 0%, transparent 65%);
    animation: ${meshDrift} 24s ${tokens.easeInOut} infinite;
    mask-image: radial-gradient(ellipse at center, black 40%, transparent 90%);
  }

  > * { position: relative; z-index: 1; }

  /* ========== HEADER ========== */
  .header-info {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 24px 0 8px 0;
    margin-bottom: 16px;
    flex-shrink: 0;

    .header-details {
      flex: 1;
      min-width: 0;

      h3 {
        margin: 4px 0 8px 0;
        font-size: 1.7em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.025em;
        line-height: 1.15;
      }

      h3::before {
        content: "PLCOM · APURAÇÃO DE IMPOSTOS";
        display: block;
        ${eyebrow};
        margin-bottom: 6px;
        opacity: 0.4;
      }

      .header-meta {
        display: flex;
        gap: 10px;
        align-items: center;
        flex-wrap: wrap;
        font-size: 0.78em;

        .status-tag {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          ${mono};
          font-size: 0.95em;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: ${tokens.brand};

          &::before {
            content: "";
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: ${tokens.brand};
            box-shadow: 0 0 8px ${tokens.brandRing};
            animation: ${pulseDot} 2.2s ${tokens.easeInOut} infinite;
          }
        }

        .update-tag {
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
          ${mono};
          font-size: 0.9em;
          &::before { content: "· "; opacity: 0.5; }
        }
      }
    }
  }

  /* ========== KPI CARDS ========== */
  .kpi-cards {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
    margin-bottom: 14px;
    flex-shrink: 0;

    @media (max-width: 1200px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 500px)  { grid-template-columns: 1fr; }

    &.hero .kpi-card {
      padding: 18px 18px 16px;

      .card-content .text .kpi-value {
        font-size: 1.85em;
        font-weight: 600;
      }
    }
  }

  .kpi-card {
    ${({ theme }) => cardBase(theme)};
    padding: 16px 18px 14px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 96px;
    position: relative;
    overflow: hidden;

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};
    }

    .card-content {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .thumb {
        opacity: 0.35;
        font-size: 0.9em;
        color: ${({ theme }) => theme.text};
        display: inline-flex;
        align-items: center;
      }

      .text {
        display: flex;
        flex-direction: column;
        gap: 2px;

        p { margin: 0; }

        .kpi-value {
          ${mono};
          order: 2;
          font-size: 1.5em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.02em;
          line-height: 1.1;
          display: flex;
          align-items: baseline;
          gap: 8px;
          margin-top: 3px;
        }

        .kpi-label {
          order: 1;
          font-size: 0.72em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          letter-spacing: -0.005em;
        }

        .kpi-caption {
          ${mono};
          order: 3;
          font-size: 0.66em;
          color: ${({ theme }) => theme.text};
          opacity: 0.32;
          margin-top: 3px;
          letter-spacing: -0.005em;
        }
      }
    }

    &.success .card-content .thumb, &.success .card-content .text .kpi-value { color: ${tokens.brand}; }
    &.warning .card-content .thumb, &.warning .card-content .text .kpi-value { color: ${tokens.warn}; }
    &.danger  .card-content .thumb, &.danger  .card-content .text .kpi-value { color: ${tokens.danger}; }
    &.success .card-content .thumb { opacity: 0.65; }
    &.warning .card-content .thumb { opacity: 0.65; }
    &.danger  .card-content .thumb { opacity: 0.65; }
  }

  .value-pill {
    ${mono};
    display: inline-flex;
    align-items: center;
    padding: 2px 7px;
    border-radius: ${tokens.radiusPill};
    font-size: 0.42em;
    font-weight: 600;
    letter-spacing: 0.02em;
    background: ${({ theme }) => surface(theme, 2)};
    color: ${({ theme }) => theme.text};
    opacity: 0.7;
    white-space: nowrap;

    &.success { background: ${tokens.brandSoft}; color: ${tokens.brand}; opacity: 1; }
    &.warning { background: ${tokens.warnSoft}; color: ${tokens.warn}; opacity: 1; }
    &.danger  { background: ${tokens.dangerSoft}; color: ${tokens.danger}; opacity: 1; }
  }

  /* ========== SECTION TITLE ========== */
  .section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 6px 0 10px 0;

    h4 {
      margin: 0;
      font-size: 0.82em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      opacity: 0.8;
      letter-spacing: -0.01em;
    }

    svg { font-size: 0.9em; opacity: 0.5; color: ${({ theme }) => theme.text}; }
  }

  /* ========== INFO BANNER ========== */
  .info-banner {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 13px 18px;
    ${({ theme }) => cardBase(theme)};
    border-radius: ${tokens.radiusMd};
    background: ${tokens.brandSoft};
    border-color: ${tokens.brandRing};
    margin-bottom: 20px;

    .banner-icon {
      flex-shrink: 0;
      width: 30px;
      height: 30px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      background: ${({ theme }) => surface(theme, 0)};
      color: ${tokens.brand};

      svg { font-size: 1em; }
    }

    .banner-text {
      display: flex;
      align-items: baseline;
      flex-wrap: wrap;
      gap: 6px;
      font-size: 0.82em;
      line-height: 1.5;

      strong {
        color: ${({ theme }) => theme.text};
        font-weight: 600;
      }

      span {
        color: ${({ theme }) => theme.text};
        opacity: 0.65;
      }
    }

    &.warning {
      background: ${tokens.warnSoft};
      border-color: rgba(245, 158, 11, 0.28);
      .banner-icon { color: ${tokens.warn}; }
    }
  }

  /* ========== RATE TABLE ========== */
  .rate-table {
    ${({ theme }) => cardBase(theme)};
    padding: 18px 20px 8px;
    margin-bottom: 20px;
    overflow-x: auto;

    h4 {
      margin: 0 0 14px 0;
      font-size: 0.9em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.01em;
    }

    table {
      width: 100%;
      border-collapse: collapse;
    }

    thead th {
      text-align: left;
      font-size: 0.68em;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: ${({ theme }) => theme.text};
      opacity: 0.4;
      padding: 0 10px 10px;
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};
      white-space: nowrap;
    }

    tbody td {
      font-size: 0.82em;
      color: ${({ theme }) => theme.text};
      padding: 12px 10px;
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};
      white-space: nowrap;

      &:first-child { font-weight: 600; }
    }

    tbody tr:last-child td { border-bottom: none; }

    tbody tr:hover td {
      background: ${({ theme }) => surface(theme, 1)};
    }

    tbody tr.row-total td {
      font-weight: 700;
      color: ${tokens.brand};
      background: ${tokens.brandSoft};
      border-top: 2px solid ${tokens.brandRing};
      border-bottom: none;
    }

    tbody tr.row-debito td:first-child {
      color: ${tokens.danger};
    }

    tbody tr.row-credito td:first-child {
      color: ${tokens.brand};
    }
  }

  .rate-tag {
    ${mono};
    display: inline-flex;
    font-size: 0.72em;
    font-weight: 500;
    padding: 3px 9px;
    border-radius: ${tokens.radiusSm};
    background: ${({ theme }) => surface(theme, 1)};
    color: ${({ theme }) => theme.text};
    opacity: 0.6;
    border: 1px solid ${({ theme }) => hairline(theme)};

    &.credito { color: ${tokens.brand}; opacity: 1; border-color: ${tokens.brandRing}; background: ${tokens.brandSoft}; }
    &.debito  { color: ${tokens.danger}; opacity: 1; border-color: ${tokens.dangerSoft}; background: ${tokens.dangerSoft}; }
  }

  /* ========== LEDGER GRID (mosaico perfeito — os blocos são distribuídos em
     3 colunas por peso/bin-packing em JS (ver distribuirEmColunas em
     index.js), align-items:stretch iguala a altura das 3 colunas, e a
     última ficha de cada coluna (.is-fill) cresce para preencher 100% do
     espaço restante, sem deixar vão em branco) ========== */
  .ledger-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    align-items: stretch;
    gap: 18px;
    margin-bottom: 20px;

    @media (max-width: 1100px) { grid-template-columns: 1fr; }

    .ledger-column {
      display: flex;
      flex-direction: column;
      gap: 18px;
    }
  }

  .ledger-block {
    ${({ theme }) => cardBase(theme)};
    padding: 16px 18px 10px;

    &.is-fill {
      flex: 1;
      display: flex;
      flex-direction: column;

      .ledger-rows { flex-shrink: 0; }
    }

    h4 {
      margin: 0 0 10px 0;
      font-size: 0.72em;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: ${({ theme }) => theme.text};
      opacity: 0.45;
    }
  }

  .ledger-rows {
    display: flex;
    flex-direction: column;
  }

  .ledger-row {
    display: flex;
    align-items: baseline;
    gap: 8px;
    padding: 9px 0;
    border-bottom: 1px solid ${({ theme }) => hairline(theme)};
    font-size: 0.82em;

    &:last-child { border-bottom: none; }

    .ledger-label {
      flex: 1;
      min-width: 0;
      color: ${({ theme }) => theme.text};
      opacity: 0.7;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .ledger-value {
      ${mono};
      flex-shrink: 0;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.01em;
    }

    &.is-total {
      margin-top: 2px;
      padding: 11px 10px;
      border-radius: ${tokens.radiusSm};
      border-bottom: none;
      background: ${tokens.brandSoft};

      .ledger-label { opacity: 1; font-weight: 700; color: ${tokens.brand}; }
      .ledger-value { font-weight: 700; color: ${tokens.brand}; }
    }
  }

  .ledger-tag {
    ${mono};
    flex-shrink: 0;
    display: inline-flex;
    font-size: 0.62em;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding: 2px 7px;
    border-radius: ${tokens.radiusPill};
    background: ${({ theme }) => surface(theme, 2)};
    color: ${({ theme }) => theme.text};
    opacity: 0.6;

    &.credito { background: ${tokens.brandSoft}; color: ${tokens.brand}; opacity: 1; }
    &.debito  { background: ${tokens.dangerSoft}; color: ${tokens.danger}; opacity: 1; }
    &.pagar   { background: ${tokens.warnSoft}; color: ${tokens.warn}; opacity: 1; }
  }

  /* ========== TABS HEADER ========== */
  .tabs-header {
    display: flex;
    gap: 8px;
    margin-bottom: 14px;
    flex-shrink: 0;
    flex-wrap: wrap;

    .tab {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 7px 14px;
      background: transparent;
      border: 1px solid transparent;
      border-radius: ${tokens.radiusMd};
      color: ${({ theme }) => theme.text};
      opacity: 0.55;
      font-size: 0.78em;
      font-weight: 500;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      ${mono};
      text-transform: uppercase;
      letter-spacing: 0.04em;

      &:hover:not(:disabled) { opacity: 0.85; background: ${({ theme }) => surface(theme, 1)}; }

      &.active {
        opacity: 1;
        background: ${tokens.brandSoft};
        border-color: ${tokens.brandRing};
        color: ${tokens.brand};
      }
    }
  }

  /* ========== BUTTONS ========== */
  .btn-primary, .btn-ghost {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 9px 18px;
    border-radius: ${tokens.radiusMd};
    font-size: 0.82em;
    font-weight: 600;
    cursor: pointer;
    transition: all ${tokens.durBase} ${tokens.easeOut};
    border: 1px solid transparent;
    white-space: nowrap;

    &:disabled { opacity: 0.4; cursor: not-allowed; }
  }

  .btn-primary {
    background: linear-gradient(135deg, ${tokens.brand}, ${tokens.brandStrong});
    color: #fff;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px ${tokens.brandGlow};
    }
  }

  .btn-ghost {
    background: ${({ theme }) => surface(theme, 1)};
    color: ${({ theme }) => theme.text};
    border-color: ${({ theme }) => hairline(theme)};

    &:hover { background: ${({ theme }) => surface(theme, 2)}; }
  }

  /* ========== PANEL CARD (genérico da apuração) ========== */
  .panel-card {
    ${({ theme }) => cardBase(theme)};
    padding: 20px 22px;
    margin-bottom: 18px;
    min-width: 0;
  }

  .panel-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px 16px;
    flex-wrap: wrap;
    margin-bottom: 14px;

    h5 {
      margin: 0;
      font-size: 0.95em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.01em;
      display: flex;
      align-items: center;
      gap: 8px;

      svg { color: ${tokens.brand}; }
    }
  }

  .panel-sub {
    margin: 3px 0 0;
    font-size: 0.78em;
    color: ${({ theme }) => theme.text};
    opacity: 0.55;
    line-height: 1.5;
  }

  .footnote {
    margin: 12px 0 0;
    font-size: 0.72em;
    color: ${({ theme }) => theme.text};
    opacity: 0.4;
  }

  .recon {
    margin-top: 14px;
    font-size: 0.8em;
    color: ${({ theme }) => theme.text};
    opacity: 0.75;
    background: ${({ theme }) => surface(theme, 1)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusSm};
    padding: 10px 12px;
    line-height: 1.5;
  }

  /* ========== SETUP (competência) ========== */
  .setup-card .setup-grid {
    display: grid;
    grid-template-columns: minmax(260px, 340px) 1fr;
    gap: 22px;
    align-items: center;

    @media (max-width: 760px) { grid-template-columns: 1fr; }
  }

  .setup-side {
    display: flex;
    flex-direction: column;
    gap: 14px;
    align-items: flex-start;

    p {
      margin: 0;
      font-size: 0.85em;
      line-height: 1.6;
      color: ${({ theme }) => theme.text};
      opacity: 0.6;
      max-width: 56ch;
    }
  }

  /* Abas das visões consolidadas do detalhe — mesma estética das
     tabs top-level, diretamente acima dos cards de semana */
  .detail-tabs { margin: 2px 0 12px; }

  /* ========== CARDS DE SEMANA — retângulos estreitos e altos,
     lado a lado começando da esquerda ========== */
  .week-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(168px, 196px));
    gap: 10px;
    margin-bottom: 18px;
  }

  .week-card {
    ${({ theme }) => cardBase(theme)};
    padding: 13px 15px;
    display: flex;
    flex-direction: column;
    gap: 9px;
    text-align: left;
    cursor: pointer;
    min-height: 104px;
    transition: all ${tokens.durBase} ${tokens.easeOut};

    .wk-head {
      display: flex;
      align-items: center;
      gap: 9px;
      min-width: 0;
    }

    .wk-badge {
      flex-shrink: 0;
      width: 28px;
      height: 28px;
      border-radius: 8px;
      border: 1px solid ${({ theme }) => hairline(theme)};
      background: ${({ theme }) => surface(theme, 1)};
      display: flex;
      align-items: center;
      justify-content: center;
      ${mono};
      font-size: 0.8em;
      font-weight: 700;
      color: ${({ theme }) => theme.text};
      transition: all ${tokens.durBase} ${tokens.easeOut};
    }

    .wk-mid {
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 1px;
    }

    .wk-name {
      font-size: 0.9em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      white-space: nowrap;
    }

    .wk-range {
      ${mono};
      font-size: 0.72em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      white-space: nowrap;
    }

    .wk-value {
      margin-top: auto;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 2px;
      border-top: 1px solid ${({ theme }) => hairline(theme)};
      padding-top: 8px;

      b {
        ${mono};
        font-size: 1.02em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        white-space: nowrap;
      }

      small {
        font-size: 0.64em;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: ${({ theme }) => theme.text};
        opacity: 0.5;
        white-space: nowrap;
      }
    }

    &:hover:not(.is-dotted):not(.selected) {
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};
    }

    &.selected {
      border-color: ${tokens.brandRing};
      background: ${tokens.brandSoft};
      box-shadow: 0 0 0 1px ${tokens.brandRing};

      .wk-badge {
        background: ${tokens.brand};
        color: #fff;
        border-color: transparent;
      }
      .wk-name { color: ${tokens.brand}; }
      .wk-range { opacity: 0.65; }
    }

    &.credor .wk-value b { color: ${tokens.info}; }

    &.is-dotted {
      border: 1.5px dashed ${({ theme }) => hairline(theme, true)};
      background: transparent;
      align-items: center;
      justify-content: center;
      text-align: center;
      gap: 5px;
      color: ${({ theme }) => theme.text};
      opacity: 0.55;

      svg { font-size: 1.3em; color: ${tokens.brand}; }
      .wk-name { font-size: 0.9em; white-space: normal; }
      .wk-range { opacity: 0.8; font-size: 0.7em; }

      &:hover {
        opacity: 1;
        border-color: ${tokens.brand};
        background: ${tokens.brandSoft};
      }
    }
  }

  .detail-content { min-width: 0; }

  /* ========== DROPZONE ========== */
  .dropzone {
    border: 1.5px dashed ${({ theme }) => hairline(theme, true)};
    border-radius: ${tokens.radiusMd};
    padding: 22px;
    display: flex;
    align-items: center;
    gap: 16px;
    cursor: pointer;
    transition: all ${tokens.durBase} ${tokens.easeOut};

    &:hover, &.dragging {
      border-color: ${tokens.brand};
      background: ${tokens.brandSoft};
    }

    .dz-icon {
      flex-shrink: 0;
      width: 44px;
      height: 44px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: ${tokens.brandSoft};
      color: ${tokens.brand};
      font-size: 1.2em;
    }

    .dz-text {
      h4 {
        margin: 0;
        font-size: 0.88em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
      }
      p {
        margin: 3px 0 0;
        font-size: 0.75em;
        color: ${({ theme }) => theme.text};
        opacity: 0.5;
      }
    }
  }

  .file-list {
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .file-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    border-radius: ${tokens.radiusSm};
    border: 1px solid ${({ theme }) => hairline(theme)};
    background: ${({ theme }) => surface(theme, 0)};

    &.accepted { border-color: ${tokens.brandRing}; }
    &.rejected { border-color: ${tokens.dangerSoft}; }

    .fi-icon {
      width: 24px; height: 24px;
      border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0;
      font-size: 0.8em;
    }
    &.accepted .fi-icon { background: ${tokens.brandSoft}; color: ${tokens.brand}; }
    &.rejected .fi-icon { background: ${tokens.dangerSoft}; color: ${tokens.danger}; }

    .fi-info {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;

      .fi-name {
        font-size: 0.8em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        display: flex; align-items: center; gap: 6px;
        svg { opacity: 0.5; }
      }
      .fi-meta {
        ${mono};
        font-size: 0.68em;
        color: ${({ theme }) => theme.text};
        opacity: 0.5;
      }
    }

    .fi-remove {
      background: none;
      border: none;
      color: ${({ theme }) => theme.text};
      opacity: 0.4;
      cursor: pointer;
      display: flex;
      &:hover { opacity: 1; color: ${tokens.danger}; }
    }
  }

  .file-list-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding-top: 8px;

    > span {
      ${mono};
      font-size: 0.72em;
      color: ${({ theme }) => theme.text};
      opacity: 0.55;
    }
  }

  @keyframes plcomSpin { to { transform: rotate(360deg); } }
  .spin { animation: plcomSpin 1s linear infinite; }

  /* ========== HERO PANEL ========== */
  .hero-panel {
    ${({ theme }) => cardBase(theme)};
    border-left: 3px solid ${tokens.brand};
    padding: 24px 26px;
    margin-bottom: 18px;
    display: grid;
    grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr);
    gap: 20px 40px;
    background:
      radial-gradient(600px 300px at 0% 0%, ${tokens.brandSoft} 0%, transparent 55%),
      ${({ theme }) => surface(theme, 0)};

    @media (max-width: 760px) { grid-template-columns: 1fr; }

    .hero-label {
      ${eyebrow};
      color: ${tokens.brand};
      opacity: 0.9;
    }

    .hero-value {
      display: block;
      ${mono};
      font-size: clamp(2em, 5vw, 3.2em);
      font-weight: 600;
      letter-spacing: -0.03em;
      line-height: 1.1;
      margin-top: 6px;
      color: ${({ theme }) => theme.text};
    }

    .hero-text {
      margin: 12px 0 0;
      font-size: 0.82em;
      color: ${({ theme }) => theme.text};
      opacity: 0.6;
      max-width: 46ch;
      line-height: 1.55;
    }

    .hero-note {
      margin: 10px 0 0;
      font-size: 0.75em;
      color: ${tokens.brand};
      max-width: 52ch;
    }
  }

  .hero-side {
    display: flex;
    flex-direction: column;

    .hs {
      padding: 14px 0;
      border-top: 1px solid ${({ theme }) => hairline(theme)};
      display: flex;
      flex-direction: column;
      gap: 2px;

      &:first-child { border-top: 0; padding-top: 0; }
      &:last-child { padding-bottom: 0; }

      .l { font-size: 0.78em; color: ${({ theme }) => theme.text}; opacity: 0.55; }
      .v { ${mono}; font-size: 1.4em; font-weight: 600; color: ${({ theme }) => theme.text}; letter-spacing: -0.01em; }
      .n { font-size: 0.72em; color: ${({ theme }) => theme.text}; opacity: 0.45; }
    }

    @media (max-width: 760px) {
      .hs:first-child { padding-top: 14px; border-top: 1px solid ${({ theme }) => hairline(theme)}; }
    }
  }

  .hero-split {
    grid-column: 1 / -1;
    padding-top: 20px;
    border-top: 1px solid ${({ theme }) => hairline(theme)};

    .split-title {
      ${eyebrow};
      display: block;
      margin-bottom: 10px;
    }

    .split-stack {
      display: flex;
      gap: 2px;
      height: 20px;

      .seg { min-width: 3px; }
      .seg:first-child { border-radius: 4px 0 0 4px; }
      .seg:last-child { border-radius: 0 4px 4px 0; }
      .seg:only-child { border-radius: 4px; }
    }

    .split-list {
      list-style: none;
      margin: 16px 0 0;
      padding: 0;
      display: grid;
      grid-template-columns: repeat(6, minmax(0, 1fr));
      gap: 14px 12px;

      @media (max-width: 860px) { grid-template-columns: repeat(3, 1fr); }
      @media (max-width: 480px) { grid-template-columns: repeat(2, 1fr); }

      li { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
      li.empty { font-size: 0.8em; color: ${({ theme }) => theme.text}; opacity: 0.45; padding: 6px 0; }

      .nm {
        font-size: 0.72em;
        color: ${({ theme }) => theme.text};
        opacity: 0.55;
        display: flex;
        align-items: center;
        gap: 6px;

        i { width: 10px; height: 10px; border-radius: 2px; flex: none; }
        i.hollow { background: transparent; border: 1.5px solid ${({ theme }) => hairline(theme, true)}; }
      }

      .vl { ${mono}; font-size: 0.95em; font-weight: 600; color: ${({ theme }) => theme.text}; }
      .vl.neg { opacity: 0.6; }
      .pc { font-size: 0.72em; color: ${({ theme }) => theme.text}; opacity: 0.45; }
    }
  }

  /* ========== GRID 2 + MINI GRID ========== */
  .grid-2 {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 18px;

    @media (max-width: 860px) { grid-template-columns: 1fr; }

    .panel-card { margin-bottom: 0; }
  }
  .grid-2 { margin-bottom: 18px; }

  .mini-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0 24px;
    margin-top: 8px;

    @media (max-width: 560px) { grid-template-columns: 1fr; }

    > div {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      padding: 9px 0;
      border-top: 1px solid ${({ theme }) => hairline(theme)};

      span:first-child { font-size: 0.8em; color: ${({ theme }) => theme.text}; opacity: 0.6; }
      b { ${mono}; font-weight: 600; font-size: 0.82em; color: ${({ theme }) => theme.text}; white-space: nowrap; text-align: right; }
    }
  }

  /* ========== TABELAS DE APURAÇÃO ========== */
  .tablewrap { overflow-x: auto; margin-top: 4px; }

  .apura-table {
    border-collapse: collapse;
    width: 100%;
    min-width: 620px;
    font-size: 0.82em;

    th, td {
      padding: 9px 10px;
      text-align: left;
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};
      white-space: nowrap;
      ${mono};
      color: ${({ theme }) => theme.text};
    }

    th {
      font-size: 0.68em;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      opacity: 0.45;
      border-bottom-color: ${({ theme }) => hairline(theme, true)};
    }

    th.r, td.r { text-align: right; }

    td .sub2 {
      display: block;
      font-size: 0.72em;
      opacity: 0.5;
      font-weight: 400;
    }

    td.neg { opacity: 0.65; }
    td.man, th.man { color: ${tokens.brand}; }
    td.man { font-weight: 600; }
    td.res { font-weight: 600; }

    tr.row-total td {
      font-weight: 700;
      border-bottom: 0;
      border-top: 1px solid ${({ theme }) => hairline(theme, true)};
      color: ${tokens.brand};
    }

    td.empty-cell {
      text-align: center;
      font-family: ${tokens.fontSans};
      opacity: 0.45;
      padding: 20px;
    }
  }

  /* ========== CRÉDITO APROVEITADO ========== */
  .legend {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 16px;
    margin-top: 4px;
    font-size: 0.72em;
    color: ${({ theme }) => theme.text};
    opacity: 0.65;

    i { display: inline-block; width: 10px; height: 10px; border-radius: 2px; margin-right: 6px; vertical-align: -1px; }
    .sw-credit { background: ${({ theme }) => theme.body === "#fafbfc" ? "#233746" : "#c1c5ca"}; }
    .sw-pay    { background: ${tokens.brand}; }
  }

  .credit-rows { display: flex; flex-direction: column; margin-top: 14px; }

  .credit-row {
    display: grid;
    grid-template-columns: 8.5rem minmax(0, 1fr) auto;
    align-items: center;
    gap: 4px 12px;
    padding: 9px 0;
    border-top: 1px solid ${({ theme }) => hairline(theme)};

    &:first-child { border-top: 0; }

    .name { font-weight: 500; font-size: 0.82em; color: ${({ theme }) => theme.text}; }

    .bar2 {
      height: 14px;
      position: relative;

      &.stack { display: flex; gap: 2px; }
      &.stack > .seg { height: 100%; min-width: 3px; }
      &.stack > .seg:first-child { border-radius: 4px 0 0 4px; }
      &.stack > .seg:last-child { border-radius: 0 4px 4px 0; }
    }

    .val {
      text-align: right;
      white-space: nowrap;
      ${mono};

      b { font-weight: 600; font-size: 0.82em; color: ${({ theme }) => theme.text}; }
      span { color: ${({ theme }) => theme.text}; opacity: 0.45; margin-left: 8px; font-size: 0.72em; }
    }

    .detail {
      grid-column: 2 / -1;
      font-size: 0.72em;
      color: ${({ theme }) => theme.text};
      opacity: 0.45;
      ${mono};
    }

    @media (max-width: 560px) {
      grid-template-columns: minmax(0, 1fr) auto;
      .bar2 { grid-column: 1 / -1; grid-row: 2; }
      .detail { grid-column: 1 / -1; }
    }
  }

  /* ========== STEPFLOW (acúmulo semana→mês — linha fina + área gradiente,
     estética iOS; fluido, sem scroll horizontal) ========== */
  @keyframes sfDotIn {
    from { opacity: 0; transform: translate(-50%, 50%) scale(0); }
    to   { opacity: 1; transform: translate(-50%, 50%) scale(1); }
  }
  @keyframes sfFadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  .stepflow {
    position: relative;
    height: 190px;
    margin: 26px 4px 48px;
  }

  .sf-svg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    overflow: visible;
    animation: sfFadeIn 0.6s ${tokens.easeOut} backwards;
  }

  .sf-line {
    stroke: ${tokens.brand};
    stroke-width: 2px;
    filter: drop-shadow(0 2px 6px ${tokens.brandGlow});
  }

  .sf-zero {
    stroke: ${({ theme }) => hairline(theme, true)};
    stroke-width: 1px;
    stroke-dasharray: 4 4;
  }

  .sf-dot {
    position: absolute;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    transform: translate(-50%, 50%);
    background: ${({ theme }) => theme.body === "#fafbfc" ? "#fff" : tokens.surface2Dark};
    border: 2.5px solid ${tokens.brand};
    z-index: 2;
    animation: sfDotIn 0.45s ${tokens.easeSpring} backwards;
    animation-delay: calc(120ms + var(--d, 0) * 90ms);

    &.down { border-color: ${tokens.danger}; }
    &.base { border-color: ${({ theme }) => hairline(theme, true)}; }
    &.tot {
      width: 14px;
      height: 14px;
      border-width: 3px;
      border-color: ${tokens.brand};
      box-shadow: 0 0 0 5px ${tokens.brandSoft}, 0 0 18px ${tokens.brandGlow};
    }
  }

  .sf-pill {
    position: absolute;
    transform: translateX(-50%);
    ${mono};
    font-size: 0.82em;
    font-weight: 700;
    white-space: nowrap;
    color: ${({ theme }) => theme.text};
    background: ${({ theme }) => surface(theme, 1)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusPill};
    padding: 4px 11px;
    backdrop-filter: blur(8px);
    box-shadow: ${tokens.shadowSm};
    z-index: 3;
    animation: sfFadeIn 0.5s ${tokens.easeOut} backwards;
    animation-delay: calc(250ms + var(--d, 0) * 90ms);
    pointer-events: none;

    &.down { color: ${tokens.danger}; border-color: ${tokens.dangerSoft}; }
    &.tot {
      color: ${tokens.brand};
      border-color: ${tokens.brandRing};
      background: ${tokens.brandSoft};
      font-size: 0.92em;
      padding: 5px 13px;
    }
  }

  .sf-names {
    position: absolute;
    left: 0; right: 0;
    top: calc(100% + 10px);
    height: 34px;
  }

  .sf-col {
    position: absolute;
    transform: translateX(-50%);
    width: 15%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1px;
    text-align: center;

    .n {
      font-size: 0.7em;
      color: ${({ theme }) => theme.text};
      opacity: 0.55;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 100%;

      &.strong { opacity: 1; font-weight: 600; }
    }

    .d {
      ${mono};
      font-size: 0.74em;
      font-weight: 700;

      &.up { color: ${tokens.brand}; }
      &.down { color: ${tokens.danger}; }
    }
  }

  /* ========== FLOWBAR (bruto → líquido — barra segmentada estilo
     "armazenamento" do iOS + checkpoints) ========== */
  .flowbox { margin-top: 20px; }

  .flow-checks {
    display: flex;
    align-items: stretch;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 16px;

    .fc {
      ${({ theme }) => cardBase(theme)};
      border-radius: ${tokens.radiusMd};
      padding: 10px 14px;
      display: flex;
      flex-direction: column;
      gap: 1px;
      min-width: 150px;
      flex: 1;

      span {
        font-size: 0.68em;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: ${({ theme }) => theme.text};
        opacity: 0.5;
      }

      b {
        ${mono};
        font-size: 1em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
      }

      small {
        font-size: 0.66em;
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
      }

      &.strong {
        border-color: ${tokens.brandRing};
        background: ${tokens.brandSoft};

        span, b { color: ${tokens.brand}; }
        small { opacity: 0.6; }
      }
    }

    .fc-arrow {
      align-self: center;
      color: ${({ theme }) => theme.text};
      opacity: 0.3;
      flex-shrink: 0;
    }
  }

  @keyframes flowSeg {
    from { transform: scaleX(0); }
    to   { transform: scaleX(1); }
  }

  .flowbar {
    display: flex;
    gap: 2px;
    height: 16px;
    border-radius: ${tokens.radiusSm};
    overflow: hidden;
    background: ${({ theme }) => surface(theme, 1)};

    .seg {
      height: 100%;
      min-width: 3px;
      transform-origin: left;
      animation: flowSeg 0.7s ${tokens.easeOut} backwards;
      transition: filter ${tokens.durBase} ${tokens.easeOut};

      &:nth-child(1) { animation-delay: 0ms; }
      &:nth-child(2) { animation-delay: 90ms; }
      &:nth-child(3) { animation-delay: 180ms; }
      &:nth-child(4) { animation-delay: 270ms; }

      &:hover { filter: brightness(1.15); }
      &:first-child { border-radius: 4px 0 0 4px; }
      &:last-child { border-radius: 0 4px 4px 0; }

      &.k-net {
        background: ${({ theme }) =>
          theme.body === "#fafbfc"
            ? "linear-gradient(180deg, #33475c, #1c2c3b)"
            : "linear-gradient(180deg, #c1c9d2, #8d99a6)"};
      }
      &.k-pay { background: linear-gradient(180deg, #34d399, ${tokens.brandStrong}); }
      &.k-out { background: rgba(239, 68, 68, 0.55); }
      &.k-adj { background: ${({ theme }) => theme.body === "#fafbfc" ? "#a1a9b0" : "#4f5f6b"}; }
    }
  }

  .flow-legend {
    list-style: none;
    margin: 14px 0 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px 14px;

    @media (max-width: 760px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 420px) { grid-template-columns: 1fr; }

    li { display: flex; flex-direction: column; gap: 1px; min-width: 0; }

    .nm {
      font-size: 0.72em;
      color: ${({ theme }) => theme.text};
      opacity: 0.6;
      display: flex;
      align-items: center;
      gap: 6px;

      i { width: 10px; height: 10px; border-radius: 3px; flex: none; }
      i.k-net { background: ${({ theme }) => theme.body === "#fafbfc" ? "#33475c" : "#b6c0ca"}; }
      i.k-pay { background: ${tokens.brand}; }
      i.k-out { background: rgba(239, 68, 68, 0.55); }
      i.k-adj { background: ${({ theme }) => theme.body === "#fafbfc" ? "#a1a9b0" : "#4f5f6b"}; }
    }

    .vl { ${mono}; font-size: 0.9em; font-weight: 600; color: ${({ theme }) => theme.text}; }
    .pc { font-size: 0.68em; color: ${({ theme }) => theme.text}; opacity: 0.45; }
  }

  /* ========== LANÇAMENTOS MANUAIS ========== */
  .manual-panel .obs-field {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 4px;

    label {
      font-size: 0.72em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
    }

    input {
      font: inherit;
      padding: 8px 10px;
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusSm};
      background: ${({ theme }) => surface(theme, 0)};
      color: ${({ theme }) => theme.text};
      width: 100%;

      &:focus-visible { outline: 2px solid ${tokens.brandRing}; outline-offset: 1px; }
    }
  }

  .row-remove {
    background: none;
    border: none;
    color: ${({ theme }) => theme.text};
    opacity: 0.35;
    cursor: pointer;
    display: inline-flex;
    font-size: 0.9em;

    &:hover { opacity: 1; color: ${tokens.danger}; }
  }

  /* ========== MODAL ADICIONAR PARÂMETRO ========== */
  .lanc-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(8, 11, 18, 0.55);
    backdrop-filter: blur(6px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 20px;
  }

  .lanc-modal {
    background: ${({ theme }) => theme.body};
    border: 1px solid ${({ theme }) => theme.border || hairline(theme)};
    border-radius: ${tokens.radiusXl};
    width: 100%;
    max-width: 460px;
    box-shadow: ${tokens.shadowLg};
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 60px);
    overflow: hidden;
    animation: plcomModalIn 0.22s ${tokens.easeOut};

    @keyframes plcomModalIn {
      from { opacity: 0; transform: translateY(10px) scale(0.985); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }
  }

  .lanc-modal-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px 14px;
    border-bottom: 1px solid ${({ theme }) => hairline(theme)};
    flex-shrink: 0;

    h3 {
      margin: 0;
      font-size: 0.95em;
      font-weight: 700;
      color: ${({ theme }) => theme.text};
      display: flex;
      align-items: center;
    }

    .close {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border-radius: 50%;
      border: none;
      background: ${({ theme }) => surface(theme, 2)};
      color: ${({ theme }) => theme.text};
      opacity: 0.6;
      cursor: pointer;

      &:hover { opacity: 1; }
    }
  }

  .lanc-modal-body {
    flex: 1;
    padding: 16px 20px 18px;
    overflow-y: auto;
  }

  /* ---------- Listas de seleção estilo iOS Settings (substituem <select>) ---------- */
  .pick-label {
    display: block;
    margin: 0 0 7px;
    font-size: 0.68em;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${({ theme }) => theme.text};
    opacity: 0.5;
  }

  .pick-list {
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    background: ${({ theme }) => surface(theme, 1)};
    overflow: hidden;
    margin-bottom: 16px;
  }

  .pick-row {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px 13px;
    border: none;
    background: transparent;
    cursor: pointer;
    text-align: left;
    transition: background ${tokens.durFast} ${tokens.easeOut};

    & + .pick-row { border-top: 1px solid ${({ theme }) => hairline(theme)}; }

    &:hover { background: ${({ theme }) => surface(theme, 2)}; }

    &.sel {
      background: ${tokens.brandSoft};

      .pr-title { color: ${tokens.brand}; }
      .pr-sub { opacity: 0.7; }
    }

    .pr-title {
      font-size: 0.82em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      line-height: 1.3;
    }

    .pr-sub {
      margin-left: auto;
      font-size: 0.68em;
      color: ${({ theme }) => theme.text};
      opacity: 0.45;
      text-align: right;
    }

    .pr-check {
      margin-left: auto;
      flex-shrink: 0;
      font-size: 0.95em;
      color: ${tokens.brand};
    }

    .pr-sub + .pr-check { margin-left: 8px; }
  }

  .lanc-note {
    margin: -8px 0 16px 2px;
    font-size: 0.72em;
    line-height: 1.5;
    color: ${({ theme }) => theme.text};
    opacity: 0.45;
  }

  /* ---------- Campos de valor por tributo (linhas .mrow do protótipo) ---------- */
  .mfield-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .mfield-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 9px 12px;
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    background: ${({ theme }) => surface(theme, 1)};
    cursor: text;

    .mf-lab {
      display: flex;
      flex-direction: column;
      gap: 1px;
      min-width: 0;
      font-size: 0.82em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};

      small {
        font-size: 0.82em;
        font-weight: 400;
        color: ${({ theme }) => theme.text};
        opacity: 0.45;
      }
    }

    .valor-field {
      margin-left: auto;
      width: 130px;
      flex-shrink: 0;
      padding: 6px 10px;
    }
  }

  /* ---------- Campo de valor ---------- */
  .valor-field {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 13px;
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    background: ${({ theme }) => surface(theme, 1)};
    transition: border-color ${tokens.durBase} ${tokens.easeOut},
                box-shadow ${tokens.durBase} ${tokens.easeOut};

    &.mini { border-radius: ${tokens.radiusSm}; }

    &:focus-within {
      border-color: ${tokens.brandRing};
      box-shadow: 0 0 0 3px ${tokens.brandSoft};
    }

    .cur {
      ${mono};
      font-size: 0.8em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      opacity: 0.4;
    }

    input {
      flex: 1;
      min-width: 0;
      border: none;
      background: transparent;
      padding: 0;
      font: inherit;
      ${mono};
      font-size: 1em;
      font-weight: 600;
      text-align: right;
      color: ${({ theme }) => theme.text};

      &::placeholder { opacity: 0.3; }
      &:focus-visible { outline: none; }
    }
  }

  .lanc-modal-foot {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 14px 20px;
    border-top: 1px solid ${({ theme }) => hairline(theme)};
    flex-shrink: 0;
  }

  /* ========== FOOTER DA ABA ========== */
  .apura-footer {
    ${mono};
    font-size: 0.7em;
    color: ${({ theme }) => theme.text};
    opacity: 0.4;
    margin: 4px 0 24px;
  }

  /* ========== SECTION CTA ========== */
  .section-cta { margin-left: auto; }

  /* ========== RELATÓRIOS (lista — mesmo padrão dos cards do Revisor) ========== */
  .relatorio-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 16px;
    margin-bottom: 20px;
  }

  .relatorio-card {
    ${({ theme }) => cardBase(theme)};
    padding: 18px 20px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    text-align: left;
    cursor: pointer;
    min-height: 150px;
    position: relative;

    &:hover {
      border-color: ${tokens.brandRing};
      background: ${({ theme }) => surface(theme, 1)};
      transform: translateY(-1px);
      box-shadow: ${tokens.shadowMd};
    }

    .rc-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 8px;

      .rc-status {
        ${mono};
        font-size: 0.62em;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        padding: 3px 9px;
        border-radius: ${tokens.radiusPill};

        &.concluido {
          background: ${tokens.brandSoft};
          color: ${tokens.brand};
          border: 1px solid ${tokens.brandRing};
        }
        &.em_andamento {
          background: ${tokens.warnSoft};
          color: ${tokens.warn};
          border: 1px solid rgba(245,158,11,0.28);
        }
        &.processando {
          background: ${tokens.brandSoft};
          color: ${tokens.brand};
          border: 1px solid ${tokens.brandRing};
        }
        &.erro {
          background: ${tokens.dangerSoft};
          color: ${tokens.danger};
          border: 1px solid rgba(239,68,68,0.3);
        }
      }

      .rc-mock {
        ${mono};
        font-size: 0.58em;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: ${({ theme }) => theme.text};
        opacity: 0.35;
      }

      .rc-icon { color: ${({ theme }) => theme.text}; opacity: 0.3; font-size: 1.05em; }
    }

    .rc-periodo {
      font-size: 1.25em;
      font-weight: 600;
      letter-spacing: -0.02em;
      color: ${({ theme }) => theme.text};
    }

    .rc-sub {
      font-size: 0.75em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
    }

    .rc-meta {
      ${mono};
      display: flex;
      gap: 14px;
      margin-top: 10px;
      font-size: 0.7em;
      color: ${({ theme }) => theme.text};
      opacity: 0.55;

      span + span::before { content: ""; }
    }

    .rc-open {
      margin-top: auto;
      padding-top: 12px;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 0.75em;
      font-weight: 600;
      color: ${tokens.brand};
    }
  }

  /* ========== DETALHE DO RELATÓRIO ========== */
  .detail-head {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 18px;

    .detail-title {
      flex: 1;
      min-width: 0;

      .eyebrow {
        ${eyebrow};
        display: block;
        margin-bottom: 2px;
      }

      h4 {
        margin: 0;
        font-size: 1.35em;
        font-weight: 600;
        letter-spacing: -0.02em;
        color: ${({ theme }) => theme.text};
      }
    }

    .pill {
      ${mono};
      font-size: 0.72em;
      border: 1px solid ${({ theme }) => hairline(theme)};
      background: ${({ theme }) => surface(theme, 0)};
      border-radius: ${tokens.radiusPill};
      padding: 4px 12px;
      color: ${({ theme }) => theme.text};
      opacity: 0.7;

      &.processing {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        opacity: 1;
        color: ${tokens.brand};
        border-color: ${tokens.brandRing};
        background: ${tokens.brandSoft};
      }
    }
  }

  /* Modal "Adicionar parâmetro": altura fixa dimensionada pelo maior
     caso (bloco com 4 tributos) — nunca muda de tamanho nem estoura;
     o espaço que sobra fica abaixo dos campos de valor */
  .lanc-modal.param-modal {
    height: min(688px, calc(100vh - 60px));

    .lanc-modal-body {
      display: flex;
      flex-direction: column;

      .mfield-list { flex-shrink: 0; }
    }
  }

  /* Modal de novo relatório um pouco mais largo */
  .lanc-modal.novo-rel-modal { max-width: 560px; }
`;
